import { useState, createContext, useMemo, } from 'react';
import { createTheme, ThemeProvider, } from '@mui/material/styles';
import { grey, blue, lightBlue, } from '@mui/material/colors';

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

const white = '#fff';
const black = '#000';
const red = '#ff0000';
const orange = '#ffa500';
const gold = '#ffd700';
const green = '#00cc00';
const darkRed = '#8b0000';
const roseWhite = '#fffbfb';
const kashmirBlue = '#4d648d';
const rhino = '#283655';
const shark = '#1e1f26';

export const ColorModeContextProvider = ({children}) => {
    const [mode, setMode] = useState('dark');

    const colorMode = useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
            },
        }),
        [],
    );

    const theme = useMemo(
        () =>
            createTheme({
                breakpoints: {
                    values: {
                        xs: 0,
                        sm: 577,
                        md: 769,
                        lg: 1025,
                    },
                },
                palette: {
                    mode,
                    common: {
                        white,
                        black,
                        red,
                        orange,
                        gold,
                        green,
                        darkRed,
                        roseWhite,
                        kashmirBlue,
                        rhino,
                        shark,
                        pattensBlue: lightBlue[50],
                    },
                    ...(mode === 'light'
                        ? {
                            primary: {
                                main: grey[200],
                            },
                            secondary: {
                                main: blue[500],
                            },
                            text: {
                                primary: grey[700],
                                secondary: grey[500],
                            },
                            background: {
                                paper: grey[200],
                                default: white,
                            },
                            divider: grey[500],
                            borderColor: {
                                light: grey[300],
                                main: grey[500],
                                dark: grey[700],
                            },
                        }
                        : {
                            primary: {
                                main: black,
                            },
                            secondary: {
                                main: blue[500],
                            },
                            text: {
                                primary: lightBlue[50],
                                secondary: grey[500],
                            },
                            background: {
                                paper: grey[900],
                                default: grey[900],
                            },
                            divider: grey[600],
                            borderColor: {
                                light: grey[300],
                                main: grey[600],
                                dark: grey[800],
                            },
                        }),
                },
                typography: {
                    logo: {
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        fontFamily: 'Roboto',
                        letterSpacing: 5,
                        fontSize: 18,
                    },
                    btn: {
                        alignItems: 'center',
                        borderRadius: 4,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        fontSize: 15,
                        fontWeight: 500,
                        justifyContent: 'center',
                        minWidth: 'auto',
                        padding: '6px 8px',
                        position: 'relative',
                        outline: 0,
                        userSelect: 'none',
                    },
                    cropped: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    },
                    default: {
                        fontFamily: 'Roboto',
                        fontSize: 16,
                        fontWeight: 'normal',
                        letterSpacing: 'normal',
                        lineHeight: 'normal',
                        textTransform: 'none',
                    },
                },
                components: {
                    MuiButton: {
                        defaultProps: {
                            disableRipple: true,
                        },
                        styleOverrides: {
                            root: {
                                textTransform: 'none',
                                transition: 'all 0s ease 0s',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            },
                        },
                    },
                    MuiIconButton: {
                        defaultProps: {
                            disableRipple: true,
                        },
                    },
                    MuiButtonGroup: {
                        defaultProps: {
                            disableRipple: true,
                        },
                    },
                    MuiListItemButton: {
                        defaultProps: {
                            disableRipple: true,
                        },
                        styleOverrides: {
                          root: {
                              '&:hover': {
                                  backgroundColor: 'transparent',
                              },
                          }
                        },
                    },
                    MuiMenuItem: {
                        defaultProps: {
                            disableRipple: true,
                        },
                    },
                    MuiTab: {
                        defaultProps: {
                            disableRipple: true,
                        },
                        styleOverrides: {
                            textTransform: 'none',
                        },
                    },
                    MuiAlert: {
                        defaultProps: {
                            sx: {
                                boxShadow: 2,
                            },
                        },
                        styleOverrides: {
                            root: {
                                display: 'inline-flex',
                                padding: '10px 20px 10px 14px',
                                wordBreak: 'break-all',
                            },
                        },
                    },
                    MuiDialog: {
                        defaultProps: {
                            sx: {
                                '& .MuiDialog-paper': {
                                    bgcolor: 'common.white',
                                    width: '100%',
                                },
                                '& .MuiDialogActions-root .MuiButton-root': {
                                    color: 'common.black',
                                    textTransform: 'none',
                                },
                            },
                        },
                    },
                    MuiDialogTitle: {
                        defaultProps: {
                            sx: {
                                bgcolor: 'common.kashmirBlue',
                            },
                        },
                    },
                    MuiDialogContentText: {
                        defaultProps: {
                            sx: {
                                color: 'grey.700',
                                mt: 3,
                                mb: 1,
                            },
                        },
                    },
                    MuiDialogActions: {
                        styleOverrides: {
                            root: {
                                bgcolor: 'common.white',
                                padding: 16,
                            },
                        },
                    },
                    MuiModal: {
                        defaultProps: {
                            sx: {
                                '& > .MuiBox-root': {
                                    bgcolor: 'background.paper',
                                    border: 1,
                                    borderColor: 'borderColor.main',
                                    borderRadius: 1,
                                    boxShadow: 5,
                                    left: '50%',
                                    outline: 'none',
                                    p: [3, 3.75, 4.5, 5.25],
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    overflow: 'auto',
                                }
                            },
                        },
                    },
                },
            }),
        [mode],
    );

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                { children }
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};